/**
 * Checks if value is `undefined`, used for type checking.
 * @internal This is a low-level utility. You probably want to use {@link isNullish} instead,
 * unless you specifically need to distinguish `undefined` from `null`.
 * @param {unknown} val
 * @returns {val is undefined}
 */
export function isUndefined(val) {
	return typeof val === "undefined";
}

/**
 * Checks if value is `null`, used for type checking.
 * @internal This is a low-level utility. You probably want to use {@link isNullish} instead,
 * unless you specifically need to distinguish `null` from `undefined`.
 * @param {unknown} val
 * @returns {val is null}
 */
export function isNull(val) {
	return val === null;
}

/**
 * Strict checking of value for null or undefined without coercion to Falsy/Truthy.
 * @param {unknown} val
 * @returns {val is null | undefined}
 */
export function isNullish(val) {
	return isUndefined(val) || isNull(val);
}

/**
 * Strict checking of value for null or undefined without coercion to Falsy/Truthy.
 * @template T
 * @param {T | null | undefined} val
 * @returns {T is NonNullable<T>}
 */
export function isNotNullish(val) {
	return !isNullish(val);
}

/**
 * Checks if the value is a literal object, i.e. not Dates, functions, arrays or null.
 * @param {unknown} val
 * @returns {val is object}
 */
export function isObject(val) {
	return Object.prototype.toString.call(val) === "[object Object]";
}

/**
 * Checks if the value is a string.
 * @param {unknown} val
 * @returns
 */
export function isString(val) {
	return typeof val === "string";
}

/**
 * Checks if the value is a function.
 * @param {unknown} val
 * @returns
 */
export function isFunction(val) {
	return typeof val === "function";
}

/**
 * Checks if a variable is empty. Supports: strings, arrays and objects.
 * @param {unknown} val
 * @returns {boolean}
 */
export function isEmpty(val) {
	if (isString(val)) {
		return val.length === 0;
	}

	if (Array.isArray(val)) {
		return val.length === 0;
	}

	// This is faster than Object.keys which could be O(n) time complexity.
	if (isObject(val)) {
		for (const prop in val) {
			if (Object.hasOwn(val, prop)) {
				return false;
			}
		}

		return true;
	}

	return false;
}

/**
 * Checks if the value is nullish or empty. The empty check works for strings, arrays and objets.
 * @param {unknown} val
 * @returns {boolean}
 */
export function isNullishOrEmpty(val) {
	return isNullish(val) || isEmpty(val);
}

/**
 * Checks if the value is nullish or empty. The empty check works for strings, arrays and objets.
 * @param {unknown} val
 * @returns {boolean}
 */
export function isNotNullishOrEmpty(val) {
	return isNotNullish(val) && !isEmpty(val);
}
